import React, { useState, useEffect } from 'react';
import { ChevronRight, Calendar, Clock, Users, BarChart3, Shield, Zap, ArrowRight, Menu, X, Star, CheckCircle, Play } from 'lucide-react';

const SmartTimetableLanding = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoginOpen, setIsLogin] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);

  const carouselSlides = [
    {
      title: "AI-Powered Scheduling",
      description: "Leverage machine learning to optimize schedules automatically",
      image: "https://www.unite.ai/wp-content/uploads/2023/09/Alex_Mc_vector_graphic_showing_scheduling_and_project_managemen_9235bb3c-bfe6-4efc-a4ba-59c77e4e6c78-1000x600.jpg",
      color: "from-purple-600 to-blue-600"
    },
    {
      title: "Real-time Collaboration",
      description: "Seamlessly coordinate with your entire organization",
      image: "https://creately.com/static/assets/help-center/realtime-collaboration/hero.webp",
      color: "from-blue-600 to-cyan-600"
    },
    {
      title: "Advanced Analytics",
      description: "Gain insights into productivity and resource utilization",
      image: "https://futransolutions.com/wp-content/uploads/2024/04/data-analytics.jpg",
      color: "from-cyan-600 to-green-600"
    },
    {
      title: "Enterprise Security",
      description: "Bank-level security with SOC 2 compliance",
      image: "https://www.bankingexchange.com/media/k2/items/cache/5ca0fa757f80a615cb73843bcc8ddb7b_XL.jpg?t=20160526_104325",
      color: "from-green-600 to-purple-600"
    }
  ];

  const features = [
    {
      icon: <Calendar className="w-8 h-8" />,
      title: "Smart Scheduling",
      description: "AI-driven conflict resolution and optimization"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Time Analytics",
      description: "Comprehensive insights into time utilization"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Team Coordination",
      description: "Seamless collaboration across departments"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Performance Metrics",
      description: "Track efficiency and productivity trends"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const featureInterval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(featureInterval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white overflow-hidden">
      {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 w-full z-50 px-6 py-4 bg-black/20 backdrop-blur-lg border-b border-white/10">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-lg flex items-center justify-center">
                        <Calendar className="w-6 h-6" />
                    </div>
                    <span className="text-xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                        ChronoPech Pro
                    </span>
                </div>

                {/* Desktop Menu */}
                <div className="hidden md:flex items-center space-x-8">
                    <a href="#features" className="hover:text-purple-300 transition-colors">Features</a>
                    <a href="#pricing" className="hover:text-purple-300 transition-colors">Pricing</a>
                    <a href="#about" className="hover:text-purple-300 transition-colors">About</a>
                    <button 
                        onClick={() => setIsLogin(true)}
                        className="px-4 py-2 rounded-lg border border-white/20 hover:bg-white/10 transition-all"
                    >
                        Login
                    </button>
                    <button onClick={() => window.location="/ttms"} className="px-6 py-2 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-lg hover:from-purple-600 hover:to-cyan-600 transition-all transform hover:scale-105">
                        Get Started
                    </button>
                </div>

                {/* Mobile Menu Button */}
                <button 
                    className="md:hidden"
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                >
                    {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {/* Mobile Menu */}
            {isMenuOpen && (
                <div className="absolute top-full left-0 right-0 bg-black/90 backdrop-blur-lg border-b border-white/10 md:hidden">
                    <div className="px-6 py-4 space-y-4">
                        <a href="#features" className="block hover:text-purple-300">Features</a>
                        <a href="#pricing" className="block hover:text-purple-300">Pricing</a>
                        <a href="#about" className="block hover:text-purple-300">About</a>
                        <button 
                            onClick={() => setIsLogin(true)}
                            className="block w-full text-left hover:text-purple-300"
                        >
                            Login
                        </button>
                        <button onClick={() => window.location="/ttms"} className="w-full px-6 py-2 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-lg">
                            Get Started
                        </button>
                    </div>
                </div>
            )}
        </nav>

      {/* Hero Section */}
      <section className="relative px-6 py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-purple-500/30">
                <Zap className="w-4 h-4 mr-2 text-purple-400" />
                <span className="text-sm">Next-Gen Scheduling Technology</span>
              </div>
              
              <h1 className="text-5xl lg:text-7xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-white via-purple-200 to-cyan-200 bg-clip-text text-transparent">
                  Smart Timetable
                </span>
                <br />
                <span className="text-white">Management</span>
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed max-w-lg">
                Revolutionize your organization's scheduling with AI-powered optimization, 
                real-time collaboration, and enterprise-grade security.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={() => window.location="/ttms"} className="group px-8 py-4 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl hover:from-purple-600 hover:to-cyan-600 transition-all transform hover:scale-105 shadow-2xl shadow-purple-500/25">
                  <span className="flex items-center text-lg font-semibold">
                    Get Started Free
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
                
                <button className="group px-8 py-4 border border-white/20 rounded-xl hover:bg-white/5 transition-all">
                  <span className="flex items-center text-lg">
                    <Play className="mr-2 w-5 h-5" />
                    Watch Demo
                  </span>
                </button>
              </div>

              <div className="flex items-center space-x-6 pt-4">
                <div className="flex items-center">
                  <div className="flex -space-x-2">
                    {[1,2,3,4].map(i => (
                      <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-400 to-cyan-400 border-2 border-slate-900" />
                    ))}
                  </div>
                  <span className="ml-3 text-sm text-gray-400">10,000+ organizations trust us</span>
                </div>
              </div>
            </div>

          {/* Carousel */}
          <div className="relative">
            <div className="relative h-96 rounded-2xl overflow-hidden backdrop-blur-sm border border-white/10"> {/* Removed initial background gradient here */}
              {carouselSlides.map((slide, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-all duration-1000 transform ${
                    index === currentSlide
                      ? 'opacity-100 translate-x-0'
                      : index < currentSlide
                        ? 'opacity-0 -translate-x-full'
                        : 'opacity-0 translate-x-full'
                  }`}
                >
                  {/* Conditional background based on slide.image existence */}
                  <div
                    className={`h-full p-8 flex flex-col justify-center items-center text-center relative`}
                    style={slide.image ? { backgroundImage: `url(${slide.image})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
                  >
                    {/* Overlay for text readability when using an image, or as the sole background for color/gradient */}
                    <div className={`absolute inset-0 ${slide.image ? 'bg-black/40' : `bg-gradient-to-br ${slide.color || 'from-gray-300 to-gray-500'}`} z-0`}></div> {/* Added fallback color if slide.color is not provided */}

                    {/* Content sits on top of the background/overlay */}
                    <div className="relative z-10 text-white"> {/* Ensure text is visible and on top */}
                      {/* If slide.image was meant to be an actual image element, place it here.
                          If it's just a URL for background, the style prop handles it.
                          If it was an emoji/icon, keep it: */}
                      {/* If slide.image is an emoji/icon string: */}
                      {typeof slide.image === 'string' && !slide.image.startsWith('http') && (
                          <div className="text-6xl mb-4">{slide.image}</div>
                      )}
                      {/* If slide.image is a URL and you want it as a visible image element instead of just background */}
                      {/* {slide.image && slide.image.startsWith('http') && (
                          <img src={slide.image} alt={slide.title} className="w-full h-auto max-h-64 object-contain mb-4" />
                      )} */}


                      <h3 className="text-2xl font-bold mb-3">{slide.title}</h3>
                      <p className="text-white/80">{slide.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
     {/* Carousel Indicators */}
              <div className="flex justify-center mt-6 space-x-2">
                {carouselSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentSlide 
                        ? 'bg-gradient-to-r from-purple-500 to-cyan-500' 
                        : 'bg-white/30'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-purple-500/20 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-cyan-500/20 rounded-full blur-xl animate-pulse delay-1000"></div>
      </section>

      {/* Features Section */}
      <section id="features" className="px-6 py-20 bg-black/20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Powerful Features
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Everything you need to manage schedules efficiently and scale your operations seamlessly.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`relative p-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 hover:border-purple-500/50 transition-all duration-500 transform hover:scale-105 ${
                  activeFeature === index ? 'ring-2 ring-purple-500/50 bg-gradient-to-br from-purple-500/10 to-cyan-500/10' : ''
                }`}
              >
                <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${
                  activeFeature === index ? 'from-purple-500 to-cyan-500' : 'from-white/10 to-white/5'
                } mb-4 transition-all duration-500`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
                
                {activeFeature === index && (
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-cyan-500/5 rounded-2xl animate-pulse" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              { number: "99.9%", label: "Uptime Guarantee", icon: <Shield className="w-8 h-8 mx-auto mb-4 text-green-400" /> },
              { number: "10K+", label: "Active Organizations", icon: <Users className="w-8 h-8 mx-auto mb-4 text-blue-400" /> },
              { number: "45%", label: "Time Savings", icon: <Clock className="w-8 h-8 mx-auto mb-4 text-purple-400" /> },
              { number: "24/7", label: "Expert Support", icon: <Star className="w-8 h-8 mx-auto mb-4 text-yellow-400" /> }
            ].map((stat, index) => (
              <div key={index} className="p-6 rounded-2xl bg-gradient-to-br from-white/5 to-transparent border border-white/10">
                {stat.icon}
                <div className="text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-20 bg-gradient-to-r from-purple-900/50 to-cyan-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            Ready to Transform Your Scheduling?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Join thousands of organizations already saving time and increasing productivity with ChronoMax Pro.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-4 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl hover:from-purple-600 hover:to-cyan-600 transition-all transform hover:scale-105 shadow-2xl">
              <span className="flex items-center text-lg font-semibold">
                Start Free Trial
                <ChevronRight className="ml-2 w-5 h-5" />
              </span>
            </button>
            <button 
              onClick={() => setIsLogin(true)}
              className="px-8 py-4 border border-white/20 rounded-xl hover:bg-white/5 transition-all"
            >
              Sign In
            </button>
          </div>
        </div>
      </section>

      {/* Login Modal */}
      {isLoginOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 w-full max-w-md border border-white/10">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold">Welcome Back</h3>
              <button 
                onClick={() => setIsLogin(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <div className="block text-sm font-medium text-gray-300 mb-2">Email</div>
                <input 
                  type="email" 
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-purple-500 focus:outline-none transition-colors"
                  placeholder="your@email.com"
                />
              </div>
              
              <div>
                <div className="block text-sm font-medium text-gray-300 mb-2">Password</div>
                <input 
                  type="password" 
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-purple-500 focus:outline-none transition-colors"
                  placeholder="••••••••"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input type="checkbox" className="mr-2" />
                  <span className="text-sm text-gray-300">Remember me</span>
                </div>
                <button className="text-sm text-purple-400 hover:text-purple-300">Forgot password?</button>
              </div>
              
              <button className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-lg hover:from-purple-600 hover:to-cyan-600 transition-all">
                Sign In
              </button>
              
              <div className="text-center">
                <span className="text-gray-400">Don't have an account? </span>
                <button className="text-purple-400 hover:text-purple-300">Sign up</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="px-6 py-12 bg-black/40 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5" />
                </div>
                <span className="text-lg font-bold">ChronoMax Pro</span>
              </div>
              <p className="text-gray-400">The future of intelligent scheduling is here.</p>
            </div>
            
            {[
              { title: "Product", items: ["Features", "Pricing", "API", "Security"] },
              { title: "Company", items: ["About", "Careers", "Blog", "Press"] },
              { title: "Support", items: ["Help Center", "Contact", "Status", "Community"] }
            ].map((column, index) => (
              <div key={index}>
                <h4 className="font-semibold mb-4">{column.title}</h4>
                <ul className="space-y-2">
                  {column.items.map((item, itemIndex) => (
                    <li key={itemIndex}>
                      <a href="#" className="text-gray-400 hover:text-white transition-colors">{item}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400">© 2025 ChronoPech Pro. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Privacy</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Terms</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Cookies</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SmartTimetableLanding;